import styled, { css } from 'styled-components';

import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import {
  cornerRadiusS,
  scale010,
  scale080,
  scale100,
  spaceS,
  spaceXS,
  spaceXXS,
  xdlColorA11yBorder,
  xdlColorControlFillTertiary,
  xdlColorFocusRing,
  xdlColorNotification,
  xdlColorPremium,
  xdlColorProBusiness,
  xdlColorProJobsFill,
  xdlColorText,
  xdlColorTextOnDark,
  xdlColorTextOnLight,
  xdlColorUserRoles,
} from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import type { FlagSize, FlagVariant } from './flag.types';

type FlagTransientProps = {
  $size: FlagSize;
  $sizeConfined?: FlagSize;
  $sizeWide?: FlagSize;
  $variant: FlagVariant;
  $isInteractive?: boolean;
};

type BodyCopyTProps = {
  $variant: FlagVariant;
};

const FLAG_BORDER_SIZE = scale010;

// *** Sizes ***
export const sizes = {
  small: css`
    height: ${scale080};
    padding-inline-end: calc(${spaceXS} - ${FLAG_BORDER_SIZE});
    padding-inline-start: calc(${spaceXS} - ${FLAG_BORDER_SIZE});
  `,
  medium: css`
    height: ${scale100};
    padding-inline-end: calc(${spaceS} - ${FLAG_BORDER_SIZE});
    padding-inline-start: calc(${spaceS} - ${FLAG_BORDER_SIZE});
  `,
};

// *** Variants ***
export const variants = {
  basic: {
    flag: css`
      background-color: ${xdlColorControlFillTertiary};
    `,
    text: css`
      color: ${xdlColorText};
    `,
  },

  premium: {
    flag: css`
      background-color: ${xdlColorPremium};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  proJobs: {
    flag: css`
      background-color: ${xdlColorProJobsFill};
    `,
    text: css`
      color: ${xdlColorTextOnLight};
    `,
  },
  insider: {
    flag: css`
      background-color: ${xdlColorUserRoles};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  // deprecated
  new: {
    flag: css`
      background-color: ${xdlColorNotification};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  // deprecated
  beta: {
    flag: css`
      background-color: ${xdlColorNotification};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  // deprecated
  ambassador: {
    flag: css`
      background-color: ${xdlColorUserRoles};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  // deprecated
  moderator: {
    flag: css`
      background-color: ${xdlColorUserRoles};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  // deprecated
  proBusiness: {
    flag: css`
      background-color: ${xdlColorProBusiness};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  // deprecated
  proCoach: {
    flag: css`
      background-color: ${xdlColorPremium};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
  // deprecated
  proTrainer: {
    flag: css`
      background-color: ${xdlColorPremium};
    `,
    text: css`
      color: ${xdlColorTextOnDark};
    `,
  },
};

// *** Components ***
export const Flag = styled.span<FlagTransientProps>`
  align-items: center !important;
  border-radius: ${cornerRadiusS};
  border: ${FLAG_BORDER_SIZE} solid
    ${({ $isInteractive }) =>
      $isInteractive ? xdlColorA11yBorder : 'transparent'};
  box-sizing: border-box;
  display: inline-flex;
  justify-content: center;
  margin: 0;
  text-decoration: none;
  user-select: none;
  white-space: nowrap;

  &:focus {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  &:focus-visible {
    outline: ${scale010} solid ${xdlColorFocusRing};
    outline-offset: ${spaceXXS};
  }

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small;
      case 'medium':
        return sizes.medium;
      default:
        return $size satisfies undefined;
    }
  }}

  @media ${mediaConfined} {
    ${({ $sizeConfined }) => {
      switch ($sizeConfined) {
        case 'small':
          return sizes.small;
        case 'medium':
          return sizes.medium;
        default:
          return $sizeConfined satisfies undefined;
      }
    }};
  }

  @media ${mediaWide} {
    ${({ $sizeWide }) => {
      switch ($sizeWide) {
        case 'small':
          return sizes.small;
        case 'medium':
          return sizes.medium;
        default:
          return $sizeWide satisfies undefined;
      }
    }};
  }

  ${({ $variant }) => {
    switch ($variant) {
      case 'basic':
        return variants.basic.flag;
      case 'premium':
        return variants.premium.flag;
      case 'proJobs':
        return variants.proJobs.flag;
      case 'insider':
        return variants.insider.flag;
      case 'new':
        return variants.new.flag;
      case 'beta':
        return variants.beta.flag;
      case 'ambassador':
        return variants.ambassador.flag;
      case 'moderator':
        return variants.moderator.flag;
      case 'proBusiness':
        return variants.proBusiness.flag;
      case 'proCoach':
        return variants.proCoach.flag;
      case 'proTrainer':
        return variants.proTrainer.flag;
      default:
        return $variant satisfies undefined;
    }
  }}
`;

export const Text = styled(BodyCopy)<BodyCopyTProps>`
  align-items: center;
  display: flex;
  justify-content: center;
  line-height: 1;

  ${({ $variant }) => {
    switch ($variant) {
      case 'basic':
        return variants.basic.text;
      case 'premium':
        return variants.premium.text;
      case 'proJobs':
        return variants.proJobs.text;
      case 'insider':
        return variants.insider.text;
      case 'new':
        return variants.new.text;
      case 'beta':
        return variants.beta.text;
      case 'ambassador':
        return variants.ambassador.text;
      case 'moderator':
        return variants.moderator.text;
      case 'proBusiness':
        return variants.proBusiness.text;
      case 'proCoach':
        return variants.proCoach.text;
      case 'proTrainer':
        return variants.proTrainer.text;
      default:
        return $variant satisfies undefined;
    }
  }}
`;
